import { create } from "@bufbuild/protobuf";
import { TimestampSchema, type Timestamp } from "@bufbuild/protobuf/wkt";
import pluralize from "pluralize";
import { match } from "ts-pattern";

export type TimeUnit =
  | "milliseconds"
  | "seconds"
  | "minutes"
  | "hours"
  | "days"
  | "months"
  | "years";

/**
 * Convert milliseconds to seconds
 * @param ms - The number of milliseconds to convert
 * @returns The number of seconds
 */
export function convertMsToSeconds(ms: number): number {
  return ms / 1000;
}

/**
 * Convert milliseconds to minutes
 * @param ms - The number of milliseconds to convert
 * @returns The number of minutes
 */
export function convertMsToMinutes(ms: number): number {
  return ms / 1000 / 60;
}

/**
 * Convert milliseconds to hours
 * @param ms - The number of milliseconds to convert
 * @returns The number of hours
 */
export function convertMsToHours(ms: number): number {
  return ms / 1000 / 60 / 60;
}

/**
 * Convert milliseconds to days
 * @param ms - The number of milliseconds to convert
 * @returns The number of days
 */
export function convertMsToDays(ms: number): number {
  return ms / 1000 / 60 / 60 / 24;
}

/**
 * Convert milliseconds to months
 * @param ms - The number of milliseconds to convert
 * @returns The number of months
 */
export function convertMsToMonths(ms: number): number {
  return ms / 1000 / 60 / 60 / 24 / 30;
}

/**
 * Convert milliseconds to years
 * @param ms - The number of milliseconds to convert
 * @returns The number of years
 */
export function convertMsToYears(ms: number): number {
  return ms / 1000 / 60 / 60 / 24 / 30 / 12;
}

/**
 * Convert milliseconds to a specific unit
 * @param ms - The number of milliseconds to convert
 * @param unit - The unit to convert to
 * @returns The converted value
 */
export function convertMsToUnit(ms: number, unit: TimeUnit): number {
  return match(unit)
    .returnType<number>()
    .with("milliseconds", () => ms)
    .with("seconds", () => convertMsToSeconds(ms))
    .with("minutes", () => convertMsToMinutes(ms))
    .with("hours", () => convertMsToHours(ms))
    .with("days", () => convertMsToDays(ms))
    .with("months", () => convertMsToMonths(ms))
    .with("years", () => convertMsToYears(ms))
    .exhaustive();
}

/**
 * Get the time ago string for a given timestamp
 * @param timestamp - The timestamp to get the time ago for
 * @returns The time ago string
 */
export function getTimeAgo(timestamp: string | Date): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const seconds = Math.floor(diff / 1000);

  if (seconds < 5) return "A few seconds ago";
  if (seconds < 60) return `Less than a minute ago`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 2) return `A minute ago`;
  if (minutes < 5) return `A few minutes ago`;
  if (minutes < 60) return `${minutes} ${pluralize("minute", minutes)} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${pluralize("hour", hours)} ago`;

  const days = Math.floor(hours / 24);
  return `${days} ${pluralize("day", days)} ago`;
}

/**
 * Get the time until string for a given timestamp
 * @param timestamp - The timestamp to get the time until for
 * @returns The time until string
 */
export function getTimeUntil(timestamp: string | Date): string {
  const diff = new Date(timestamp).getTime() - Date.now();
  const seconds = Math.floor(diff / 1000);

  if (seconds < 5) return "In a few seconds";
  if (seconds < 60) return "In less than a minute";

  const minutes = Math.floor(seconds / 60);
  if (minutes < 2) return "In a minute";
  if (minutes < 5) return "In a few minutes";
  if (minutes < 60) return `In ${minutes} ${pluralize("minute", minutes)}`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `In ${hours} ${pluralize("hour", hours)}`;

  const days = Math.floor(hours / 24);
  return `In ${days} ${pluralize("day", days)}`;
}

/**
 * Format a date string to a readable date string
 * @param date - The date string to format
 * @returns The formatted date string
 */
export function formatDate(date: string): string {
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Format a time in milliseconds to a readable time string
 * @param ms - The time in milliseconds to format
 * @returns The formatted time string
 */
export function formatTimeMs(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

/**
 * Format a date string to a readable time string
 * @param date - The date string to format
 * @returns The formatted time string
 */
export function formatTime(date: string): string {
  return new Date(date).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Format a date string to a readable date and time string
 * @param date - The date string to format
 * @returns The formatted date and time string
 */
export function formatDateTime(date: string): string {
  return `${formatDate(date)} ${formatTime(date)}`;
}

/**
 * Create a protobuf timestamp
 * @returns The protobuf timestamp
 */
export const generateProtobufTimestamp = (timestamp: number = Date.now()): Timestamp => {
  return create(TimestampSchema, {
    seconds: BigInt(Math.floor(timestamp / 1000)),
    nanos: 0,
  });
};

/**
 * Convert a protobuf timestamp to a date string
 * @param timestamp - The protobuf timestamp to convert
 * @returns The date string
 */
export function convertProtobufTimestampToDate(timestamp: Timestamp): Date {
  return new Date(Number(timestamp.seconds) * 1000);
}
